import { useCallback, useMemo, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { MediumModalCreateEdit } from '../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { sortCustomerProjectStatuses } from '../../../../../../../specificComponents/Customers/SortCustomerProjectStatuses'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import { EmployeesPayload } from '../../../../../../../utils/reducers/company/employees'
import { GroupPayload } from '../../../../../../../utils/reducers/groups/groups'
import { BeforeSubmitHandler } from '../../../../../../../forms/TwinForm/types'
import CustomSelectSectionCustomers from '../../../../../../../specificComponents/Customers/CustomSelectSectionCustomers'
import { CustomerProjectTypePayload, DictionaryCustomerProject } from '../../../../../../../utils/reducers/customers/customerProjectTypes'
import { CustomerPayload } from '../../../../../../../utils/reducers/customers/customer'
import { useGetCustomerProjectTypeByIsMain } from '../../../../../../../utils/hooks/customer/useProjectsAndOpportunitiesDict'
import { addDictionaryExtraLine } from '../../../../../../../utils/globals/dictionary'
import { emptyExtraLineFirstSortFunction } from '../../../../../../../forms/CustomSelect/functions'
import { CustomerProjectAddModelTypeExtended } from '../types'
import { projectTypesInversed, ProjectTypesKeys } from '@teinor/erp/types/company/customer/customerProject'
import { TChecklistsPayload } from '../../../../../../../utils/reducers/customers/tChecklists'


type ModalCreateCustomerProjectProps = ModalEditComponentProps & ReduxCustomerProject & {}

const ModalCreateCustomerProject: React.FC<ModalCreateCustomerProjectProps> = ({ onSubmit, setOpened, employees, groups, customers, customerProjectTypes, tChecklists }) => {
    const { t } = useTwinTranslation()
    const { myProjectStatuses, setMyProjectTypeId, myProjectTypeId, handleBeforeSubmit } = useModalCreateCustomerProjectLogic({ customerProjectTypes })
    const checkListExtraLineDict = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(tChecklists || {}))), [tChecklists])
    const fields: FormRender<CustomerProjectAddModelTypeExtended> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'name',
                    component: 'InputWithLabelMargin',
                    label: t('name', 'Nombre'),
                    required: true
                },
                {
                    name: 'description',
                    component: 'TextArea',
                    label: t('description', 'Descripción'),
                },
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: ({ extraProps }) => <CustomSelectSectionCustomers name='CustomerId' customers={extraProps.customers} label={t('customer', 'Cliente')} required={true} />,
                    extraProps: { customers }
                },
                {
                    name: 'CustomerProjectTypeId',
                    component: 'CustomSelectWithSearchBar',
                    required: true,
                    label: t('projectType', 'Tipo de proyecto'),
                    items: customerProjectTypes || {},
                    value: myProjectTypeId,
                    onChange: (value) => setMyProjectTypeId(value)
                },
                {
                    name: 'CurrentCProjectStatusId',
                    component: 'CustomSelectColor',
                    label: t('projectStatus', 'Tipo de estado'),
                    items: myProjectStatuses || [],
                    value: customerProjectTypes?.[parseInt(myProjectTypeId || '0')]?.CustomerProjectStatusesByType?.project?.[0]?.id || '',
                    extraDropDownParams: {
                        sortFunction: sortCustomerProjectStatuses
                    }
                },
                {
                    name: 'TChecklistId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('defaultList', 'Lista por defecto'),
                    items: checkListExtraLineDict || {},
                    sortFunction: emptyExtraLineFirstSortFunction,
                    value: customerProjectTypes?.[parseInt(myProjectTypeId || '')]?.CPChecklistId,
                },
                {
                    name: 'limit_date',
                    label: t('limitDate', 'Fecha límite'),
                    component: 'InputCalendarStateFull',
                    onlyValids: true,
                },
                {
                    name: 'projectType',
                    component: 'InputHidden',
                    value: 1,
                }
            ]
        },
        {
            cols: 1,
            title: t('employees', 'Empleados'),
            elements: [
                {
                    name: 'Employees',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('notSelected', 'Sin seleccionar'), subtitleSelectedOptions: t('selected', 'Seleccionados') },
                    items: employees || {},
                }
            ]
        },
        {
            cols: 1,
            title: t('groups', 'Grupos'),
            elements: [
                {
                    name: 'Groups',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('notSelected', 'Sin seleccionar'), subtitleSelectedOptions: t('selected', 'Seleccionados') },
                    items: groups || {},
                }
            ]
        }
    ]
    return (
        <MediumModalCreateEdit url='/api/app/customer/customerProject/createCustomerProject' fields={fields} setOpened={setOpened} onSubmit={onSubmit} translations={{
            title: t('createProject', 'Crear proyecto'),
            button: t('create', 'Crear')
        }} haveButtonPermissions={true} beforeSubmit={handleBeforeSubmit}>
        </MediumModalCreateEdit>
    )
}

interface ModalCreateCustomerProjectLogicProps {
    customerProjectTypes: DictionaryCustomerProject | null
}

const useModalCreateCustomerProjectLogic = ({ customerProjectTypes }: ModalCreateCustomerProjectLogicProps) => {
    const { getCustomerProjectTypeByIsMain } = useGetCustomerProjectTypeByIsMain()
    const customerProjectTypeByIsMain = useMemo(() => getCustomerProjectTypeByIsMain(customerProjectTypes, projectTypesInversed['project'] as ProjectTypesKeys), [customerProjectTypes, getCustomerProjectTypeByIsMain])
    const [myProjectTypeId, setMyProjectTypeId] = useState<string | undefined>(customerProjectTypeByIsMain?.id)
    const myProjectStatuses = useMemo(() => {
        return myProjectTypeId ? customerProjectTypes?.[parseInt(myProjectTypeId)].customerProjectStatusDict.project : {}
    }, [myProjectTypeId, customerProjectTypes])

    const handleBeforeSubmit: BeforeSubmitHandler = useCallback((values: any) => {
        if (values.CurrentCProjectStatusId && !myProjectStatuses?.[values.CurrentCProjectStatusId]) {
            values.CurrentCProjectStatusId = null
        }
        return true
    }, [myProjectStatuses])

    return {
        myProjectStatuses, setMyProjectTypeId, myProjectTypeId, handleBeforeSubmit }
}


const customerProjectDispatch = {
    setGroups: (payload: GroupPayload) => ({ type: 'CHANGE_GROUP', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
    setCustomers: (payload: CustomerPayload) => ({ type: 'CHANGE_CUSTOMER', payload }),
    setCustomerProjectTypes: (payload: CustomerProjectTypePayload) => ({ type: 'CHANGE_CUSTOMER_PROJECT_TYPES', payload }),
    setTChecklists: (payload: TChecklistsPayload) => ({ type: 'CHANGE_T_CHECKLISTS', payload })
}

export type ReduxCustomerProject = ConnectedProps<typeof customerProjectConnect>
const mapCustomerProjectTypesConnector = (state: AllReduxPayloads) => ({ groups: state.groups, employees: state.employees, customers: state.customers, customerProjectTypes: state.customerProjectTypes, tChecklists: state.tChecklists })
const customerProjectConnect = connect(mapCustomerProjectTypesConnector, customerProjectDispatch)

const customerProjectConnectLoading = withLoading(ModalCreateCustomerProject, [{ fetchUrl: '/api/app/group/getAllGroupsListing', propName: 'groups', setFunctionName: 'setGroups' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }, { fetchUrl: '/api/app/customer/getAllCustomersComplete', propName: 'customers', setFunctionName: 'setCustomers' }, { fetchUrl: '/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypesFull', propName: 'customerProjectTypes', setFunctionName: 'setCustomerProjectTypes' }, { fetchUrl: '/api/app/config/templateChecklist/getAllTChecklists', propName: 'tChecklists', setFunctionName: 'setTChecklists' }])

const ModalCreateCustomerProjectsConnect = customerProjectConnect(customerProjectConnectLoading)

export default ModalCreateCustomerProjectsConnect